import { useEffect, useState } from "react";
import { useLoadScript } from "@react-google-maps/api";
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from "use-places-autocomplete";
import {
  Box,
  TextField,
  Autocomplete,
  Grid,
  Typography,
  useTheme,
} from "@mui/material";
import parse from "autosuggest-highlight/parse";
import { BCPLocationDot } from "../icons/bcp/BCPIcons";
import { getEnvironmentVariable } from "../../global/environmentVariables";
import { googleTimezoneApi } from "./UtilityFunctions";

const libraries = ["places"];

export default function GoogleAddressSearch(props) {
  const { label, locationData, setLocationData, initialSearch, sx, onBlur } =
    props;
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: getEnvironmentVariable("GOOGLE_API_KEY"),
    libraries,
  });

  const [center, setCenter] = useState({
    lat: 41.850033,
    lng: -87.6500523,
  });

  useEffect(() => {
    if (!isLoaded) return;
    if (locationData?.coordinate) {
      setCenter({
        lat: locationData.coordinate[1],
        lng: locationData.coordinate[0],
      });
    } else {
      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setCenter({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            });
          },
          () => null
        );
      }
    }
    // eslint-disable-next-line
  }, [props, isLoaded]);

  if (loadError) return "Error loading maps";
  if (!isLoaded) return "Loading Maps";

  return (
    <Search
      label={label}
      center={center}
      initialSearch={initialSearch}
      setLocationData={setLocationData}
      sx={sx}
      onBlur={onBlur}
    />
  );
}

function Search(props) {
  const { label, center, initialSearch, setLocationData } = props;
  const {
    value,
    suggestions: { data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      location: {
        lat: () => center.lat,
        lng: () => center.lng,
      },
      radius: 200 * 1000,
    },
  });
  const theme = useTheme();

  const handleInput = (e) => {
    setValue(e.target.value);
  };

  const handleSelect = async (event, address) => {
    setValue(address, false);
    clearSuggestions();

    try {
      const results = await getGeocode({ address: address?.description });
      const result = await getLatLng(results[0]);
      const { lat, lng } = result;
      const timezone = await googleTimezoneApi({
        location: `${lat},${lng}`,
        key: getEnvironmentVariable("GOOGLE_API_KEY"),
      });

      var loc = {
        coordinate: [lng, lat],
        formatted_address: results[0]?.formatted_address,
      };

      if (timezone?.timeZoneId) loc.timeZone = timezone.timeZoneId;
      if (results[0]?.address_components) {
        results[0].address_components.forEach(
          ({ long_name, short_name, types }) => {
            if (types.includes("subpremise")) loc.subPremise = short_name;
            if (types.includes("street_number")) loc.streetNum = short_name;
            if (types.includes("route")) loc.streetName = long_name;
            if (types.includes("locality")) loc.city = long_name;
            if (types.includes("administrative_area_level_1"))
              loc.state = short_name;
            if (types.includes("country")) loc.country = long_name;
            if (types.includes("postal_code")) loc.zip = short_name;
          }
        );
      }
      setLocationData(loc);
    } catch (error) {
      console.error("Error: ", error);
    }
  };

  useEffect(() => {
    setValue(initialSearch, false);
    // eslint-disable-next-line
  }, [initialSearch]);

  return (
    <Box>
      <Autocomplete
        id="googleMapSearchBox"
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.description
        }
        disablePortal
        filterOptions={(x) => x}
        options={data}
        autoComplete
        includeInputInList
        filterSelectedOptions
        value={value}
        noOptionsText="No locations"
        onChange={handleSelect}
        onInputChange={handleInput}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            margin="normal"
            label={label || "Search Location"}
            fullWidth
          />
        )}
        renderOption={(props, option) => {
          const matches =
            option.structured_formatting.main_text_matched_substrings || [];

          const parts = parse(
            option.structured_formatting.main_text,
            matches.map((match) => [match.offset, match.offset + match.length])
          );

          return (
            <li {...props}>
              <Grid container alignItems="center">
                <Grid item sx={{ display: "flex", width: 44 }}>
                  <BCPLocationDot
                    style={{ color: theme?.palette?.text?.secondary }}
                  />
                </Grid>
                <Grid
                  item
                  sx={{ width: "calc(100% - 44px)", wordWrap: "break-word" }}
                >
                  {parts.map((part, index) => (
                    <Box
                      key={index}
                      component="span"
                      sx={{ fontWeight: part.highlight ? "bold" : "regular" }}
                    >
                      {part.text}
                    </Box>
                  ))}

                  <Typography variant="body2" color="text.secondary">
                    {option.structured_formatting.secondary_text}
                  </Typography>
                </Grid>
              </Grid>
            </li>
          );
        }}
      />
    </Box>
  );
}
